import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useInjectSaga } from 'redux-injectors';
import { useIntl } from 'react-intl';

import { Editor } from 'models/Intervention';
import { useSessionPing } from 'utils/useSessionPing';

import {
  SocketErrorMessageData,
  SocketMessageListener,
  useSocket,
} from 'utils/useSocket';
import objectToCamelCase from 'utils/objectToCamelCase';

import {
  refreshInterventionData,
  resetCollaborationState,
  setCurrentEditor,
  setStartingEditing,
  setStoppingEditing,
  withRefreshInterventionDataSaga,
} from 'global/reducers/intervention';

import {
  InterventionChannelMessage,
  EditingStartedData,
  InterventionChannelAction,
  InterventionChannelConnectionParams,
  UnexpectedErrorData,
  ForceEditingStartedData,
} from './types';
import {
  INTERVENTION_CHANNEL_NAME,
  InterventionChannelActionName,
  InterventionChannelMessageTopic,
} from './constants';
import messages from './messages';

export type InterventionChannel = ReturnType<typeof useInterventionChannel>;

export const useInterventionChannel = (interventionId?: string) => {
  const dispatch = useDispatch();
  const { sendPing } = useSessionPing();
  const { formatMessage } = useIntl();

  useInjectSaga(withRefreshInterventionDataSaga);

  const showErrorToast = ({ error }: SocketErrorMessageData) => {
    toast.error(error);
  };

  const onEditingStarted = ({ current_editor }: EditingStartedData) => {
    const currentEditor: Editor = objectToCamelCase(current_editor);
    dispatch(setCurrentEditor(currentEditor));
    dispatch(setStartingEditing(false));
    sendPing();
  };

  const onEditingStopped = () => {
    dispatch(refreshInterventionData(interventionId!, false));
    dispatch(setCurrentEditor(null));
    dispatch(setStoppingEditing(false));
    sendPing();
  };

  const onForceEditingStarted = ({
    current_editor,
  }: ForceEditingStartedData) => {
    const currentEditor: Editor = objectToCamelCase(current_editor);
    dispatch(setCurrentEditor(currentEditor));
    dispatch(setStartingEditing(false));
    // refreshes intervention data for the user forcing editing too to get
    // latest changes made by the user that was forced to stop editing
    dispatch(refreshInterventionData(interventionId!, true));
    sendPing();
  };

  const onUnexpectedError = (errorData: UnexpectedErrorData) => {
    showErrorToast(errorData);
    dispatch(setStoppingEditing(false));
    dispatch(setStartingEditing(false));
  };

  const messageListener: SocketMessageListener<InterventionChannelMessage> = ({
    data,
    topic,
  }) => {
    switch (topic) {
      case InterventionChannelMessageTopic.UNEXPECTED_ERROR:
        onUnexpectedError(data);
        break;
      case InterventionChannelMessageTopic.EDITING_STARTED:
        onEditingStarted(data);
        break;
      case InterventionChannelMessageTopic.EDITING_STOPPED:
        onEditingStopped();
        break;
      case InterventionChannelMessageTopic.FORCE_EDITING_STARTED:
        onForceEditingStarted(data);
        break;
      default:
        break;
    }
  };

  const onUnsubscribe = () => {
    dispatch(resetCollaborationState());
  };

  const channel = useSocket<
    InterventionChannelMessage,
    InterventionChannelAction,
    InterventionChannelConnectionParams
  >(INTERVENTION_CHANNEL_NAME, messageListener, {
    socketConnectionParams: { id: interventionId },
    suspend: !interventionId,
    onUnsubscribe,
  });

  const startEditing = async () => {
    dispatch(setStartingEditing(true));
    sendPing();

    try {
      await channel?.perform({
        name: InterventionChannelActionName.ON_EDITING_STARTED,
        data: {},
      });
    } catch (error) {
      dispatch(setStartingEditing(false));
      toast.error(formatMessage(messages.startEditModeError));
    }
  };

  const stopEditing = async () => {
    dispatch(setStoppingEditing(true));
    sendPing();

    try {
      await channel?.perform({
        name: InterventionChannelActionName.ON_EDITING_STOPPED,
        data: {},
      });
    } catch (error) {
      dispatch(setStoppingEditing(false));
      toast.error(formatMessage(messages.stopEditModeError));
    }
  };

  const forceStartEditing = async () => {
    dispatch(setStartingEditing(true));
    sendPing();

    try {
      await channel?.perform({
        name: InterventionChannelActionName.ON_FORCE_EDITING_STARTED,
        data: {},
      });
    } catch (error) {
      dispatch(setStartingEditing(false));
      toast.error(formatMessage(messages.startEditModeError));
    }
  };

  return {
    startEditing,
    stopEditing,
    forceStartEditing,
    interventionId,
  };
};
